import type { Draft, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';

import { assertUnreachable } from '@disy/lib/custom-error';
import { cadenzaSessionStorage } from '@disy/lib/storage';
import { entries } from '@disy/lib/type-util';

import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { booleanFromSessionStorage } from 'cadenza/store/state-utils';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import {
  WorkbookPanelName,
  getPanelVisibilityFromDisplayState,
  initializePanelVisibility,
} from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

interface PanelState {
  visible: boolean;
  disabled: boolean;
}

export type PanelById = Record<WorkbookPanelName, PanelState>;

const PANELS_PARAM = 'panels';

initializePanelVisibility(PANELS_PARAM); // side-effect!

export function createInitialPanelState() {
  const panelById = Object.fromEntries(
    Object.values(WorkbookPanelName).map((panel) => [
      panel,
      {
        visible: booleanFromSessionStorage(panel, true),
        disabled: false,
      },
    ]),
  ) as PanelById;
  return { panelById };
}

export function persistPanelState(state: WorkbookState) {
  Object.values(WorkbookPanelName).forEach((panelId) => {
    cadenzaSessionStorage.setItem(panelId, String(state.panelById[panelId].visible));
  });
}

// Reducers

export const panelReducers = {
  togglePanelVisibility: {
    prepare: (panelId: WorkbookPanelName, visible?: boolean) => ({ payload: { panelId, visible } }),
    reducer: (
      state,
      {
        payload: { panelId, visible = !state.panelById[panelId].visible },
      }: PayloadAction<{
        panelId: WorkbookPanelName;
        visible?: boolean;
      }>,
    ) => {
      const previousVisibility = isPanelVisible(state, panelId);
      state.panelById[panelId].visible = Boolean(visible);
      state.clientChanged = isPanelVisible(state, panelId) !== previousVisibility;
    },
  },
  hideAllPanels(state) {
    Object.values(state.panelById).forEach((panel) => {
      panel.visible = false;
    });
  },
} satisfies SliceCaseReducers<WorkbookState>;

// Helper reducers

export function updatePanelStateFromDisplayState(state: Draft<WorkbookState>) {
  const panelVisibility = getPanelVisibilityFromDisplayState(state.displayState);
  const workbookWasOpenedFromNavigator = panelVisibility != null;
  if (workbookWasOpenedFromNavigator) {
    entries(panelVisibility).forEach(([panelId, visible]) => {
      state.panelById[panelId].visible = visible;
    });
  }
  for (const panelId of Object.values(WorkbookPanelName)) {
    state.panelById[panelId].visible = isWorkbookFeatureForPanelAvailable(state, panelId);
  }
}

function isWorkbookFeatureForPanelAvailable(state: Draft<WorkbookState>, panelId: WorkbookPanelName) {
  switch (panelId) {
    case 'd-analysis-context-panel':
      return isPanelVisible(state, panelId) && isWorkbookFeatureAvailable(state, 'workbook-analysis-context');
    case 'd-view-settings-panel':
      return isPanelVisible(state, panelId) && isWorkbookFeatureAvailable(state, 'workbook-design');
    case 'd-data-browser-panel':
      return isPanelVisible(state, panelId) && isWorkbookFeatureAvailable(state, 'workbook-data-browser');
    default:
      assertUnreachable(panelId);
  }
}

export function forcePanelsHidden(state: Draft<WorkbookState>) {
  Object.values(WorkbookPanelName).forEach((panelId) => {
    state.panelById[panelId] = { visible: false, disabled: true };
  });
}

// Selectors

export const isPanelVisible = (state: RootStateOr<WorkbookState>, panelId: WorkbookPanelName) =>
  slice(state).panelById[panelId].visible;
export const isPanelDisabled = (state: RootStateOr<WorkbookState>, panelId: WorkbookPanelName) =>
  slice(state).panelById[panelId].disabled;
